<template>
	<div class="ms_wrapper" ref="root">
		<button type="button"
			class="ms_trigger"
			:class="{ 'is-active': isActive, 'is-open': isOpen, 'ms_trigger--segmented': segmented }"
			:aria-expanded="isOpen ? 'true' : 'false'"
			aria-haspopup="listbox"
			:disabled="disabled"
			@click.stop="toggle">
			<span class="ms_label">{{ placeholder }}</span>
			<span v-if="value.length > 0 && !single" class="ms_count_badge">{{ value.length }}</span>
			<ArrowDown class="ms_chevron" :class="{ 'is-rotated': isOpen }" />
		</button>

		<transition name="ms_fade">
			<div v-if="isOpen" class="ms_panel"
				:class="{ 'is-right': align === 'right' }"
				role="listbox" :aria-multiselectable="single ? 'false' : 'true'" @click.stop>
				<div v-if="searchable" class="ms_search">
					<input ref="search"
						v-model="query"
						type="text"
						class="ms_search_input"
						:placeholder="searchPlaceholder"
						@click.stop />
				</div>
				<ul class="ms_options">
					<template v-for="entry in displayRows">
						<!-- Encabezado de grupo seleccionable: marca/desmarca de una
							todos los items del grupo. Checkbox con estado all (✓),
							some (indeterminado, –) o none. -->
						<li v-if="entry.type === 'header'" :key="'h-' + entry.label"
							class="ms_group_header"
							role="option"
							:aria-selected="isGroupChecked(entry.label) ? 'true' : 'false'"
							@click.stop="toggleGroup(entry.label)">
							<span class="ms_check"
								:class="{
									'is-checked': isGroupChecked(entry.label),
									'is-indeterminate': isGroupIndeterminate(entry.label),
								}">
								<span class="ms_check_mark" v-if="isGroupChecked(entry.label)"></span>
								<span class="ms_check_dash" v-else-if="isGroupIndeterminate(entry.label)"></span>
							</span>
							<span class="ms_group_label">{{ entry.label }}</span>
						</li>
						<li v-else :key="entry.opt.id"
							class="ms_option"
							:class="{ 'is-checked': isSelected(entry.opt.id), 'ms_option--child': grouped }"
							role="option"
							:aria-selected="isSelected(entry.opt.id) ? 'true' : 'false'"
							@click.stop="toggleOption(entry.opt.id)">
							<span class="ms_check"
								:class="{ 'is-checked': isSelected(entry.opt.id), 'ms_check--radio': single }">
								<span class="ms_check_mark" v-if="isSelected(entry.opt.id)"></span>
							</span>
							<!-- Label recortado a un límite fijo de caracteres
								(LABEL_MAX_LEN). El nombre completo va en un tooltip
								estilizado (floating-vue, no `title` nativo) sólo si lo
								supera. -->
							<span class="ms_option_label"
								v-tooltip.right="optionTooltip(entry.opt.label)">{{ truncateLabel(entry.opt.label) }}</span>
						</li>
					</template>
					<li v-if="visibleOptions.length === 0" class="ms_empty">
						Sin resultados
					</li>
				</ul>
				<p v-if="hiddenCount > 0" class="ms_overflow_hint">
					Mostrando {{ visibleOptions.length }} de {{ matchedCount }} — refina tu búsqueda
				</p>
				<div v-if="value.length > 0 && !isAllSelected" class="ms_footer">
					<button type="button"
						class="ms_clear_btn"
						@click.stop="clearAll">
						{{ clearLabel }}
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import ArrowDown from "@/components/svg/ArrowDown.vue";

// Límite fijo de caracteres del label de cada opción: se recorta el texto a
// `LABEL_MAX_LEN` (no por ancho dinámico/ellipsis) y, si el nombre original es
// más largo, se muestra completo en un tooltip estilizado al hacer hover.
const LABEL_MAX_LEN = 25;

export default {
	name: "MultiSelectDropdown",

	components: { ArrowDown },

	model: {
		prop: "value",
		event: "input",
	},

	props: {
		value: {
			type: Array,
			default: () => [],
		},
		options: {
			type: Array,
			required: true,
			validator: (arr) => arr.every(o => o && o.id !== undefined && o.label !== undefined),
		},
		placeholder: {
			type: String,
			default: "Seleccionar",
		},
		clearLabel: {
			type: String,
			default: "Ver todos",
		},
		// Opt-in: agrega un input de búsqueda arriba de la lista. Pensado para
		// listas largas (cientos/miles de items, p.ej. clientes). Los call sites
		// existentes (sin esta prop) renderean igual que antes.
		searchable: {
			type: Boolean,
			default: false,
		},
		searchPlaceholder: {
			type: String,
			default: "Buscar…",
		},
		// Tope de items renderados a la vez. Con listas de miles, renderear todos
		// los <li> traba el dropdown; capeamos y guiamos al usuario a afinar la
		// búsqueda. El filtrado corre sobre el set completo, sólo se acota el
		// render.
		maxVisible: {
			type: Number,
			default: 200,
		},
		// Lado al que se ancla el panel desplegado: "left" (default) abre desde
		// el borde izquierdo del trigger; "right" desde el derecho. Útil cuando
		// el trigger vive pegado al borde derecho y un panel left-anclado se
		// saldría de pantalla.
		align: {
			type: String,
			default: "left",
			validator: (v) => ["left", "right"].includes(v),
		},
		// Opt-in: renderiza encabezados de grupo. Cada option debe traer un campo
		// `group` (string) y la lista debe venir ordenada por grupo — el header se
		// inserta al cambiar de `group`. La selección sigue siendo plana (por id).
		grouped: {
			type: Boolean,
			default: false,
		},
		// Opt-in: convierte al trigger en un toggle "todo o nada". Al abrir el
		// panel desde una selección vacía, marca TODOS los items por default (emite
		// todos los ids) en vez de abrir sobre una lista en blanco. Pensado para
		// dimensiones presionables (p.ej. tipos de contraparte en Gastos): un click
		// activa la dimensión completa y el panel queda abierto para deseleccionar.
		// Si ya hay selección, no la pisa.
		selectAllOnOpen: {
			type: Boolean,
			default: false,
		},
		// Opt-in: renderiza el trigger como un chip de control segmentado
		// (transparente, primary al activarse) para vivir flush dentro de un
		// grupo .sub_nav_filters, en vez del botón blanco con borde por default.
		// Conserva el chevron de desplegable. Lo usa Gastos para que el filtro
		// de tipos de contraparte se vea como "Clientes" en Ventas.
		segmented: {
			type: Boolean,
			default: false,
		},
		// Opt-in: selección ÚNICA. Elegir un item reemplaza al anterior (emite
		// `[id]`) y cierra el panel; volver a elegirlo lo quita (emite `[]`,
		// que para los call sites significa "todos"). Las opciones se dibujan
		// como radios y el trigger no muestra el contador. Lo usa el drawer de
		// Rentabilidad: un solo ítem de la categoría a la vez (Steve,
		// 2026-10-07). No se combina con `grouped` ni `selectAllOnOpen`.
		single: {
			type: Boolean,
			default: false,
		},
		// Opt-in: el trigger no abre (p. ej. un select que espera a otro).
		disabled: {
			type: Boolean,
			default: false,
		},
		// Tope de caracteres del label de cada opción antes de recortar con
		// elipsis (el nombre completo va al tooltip). Los call sites de filtros
		// conservan el límite histórico; un selector de formulario con nombres
		// largos (cuentas del plan) lo sube.
		labelMaxLen: {
			type: Number,
			default: LABEL_MAX_LEN,
		},
	},

	data() {
		return { isOpen: false, query: "" };
	},

	computed: {
		isActive() { return this.value.length > 0; },

		// Todos los items marcados: ocultamos "Ver todos" (limpiar) porque no
		// recorta nada — ya está mostrando el set completo.
		isAllSelected() {
			if (!this.options.length) return false;
			const selected = new Set(this.value);
			return this.options.every((opt) => selected.has(opt.id));
		},

		// Precomputa la forma normalizada (sin acentos, lowercase) de cada label
		// una sola vez por cambio de `options`. Filtrar miles de items por
		// keystroke se vuelve un substring-check sobre strings ya normalizados.
		normalizedOptions() {
			return this.options.map((opt) => ({
				opt,
				norm: this.normalize(String(opt.label)),
			}));
		},

		// Items que matchean la query (set completo, sin capear).
		matchedOptions() {
			const q = this.normalize(this.query.trim());
			if (!q) return this.options;
			return this.normalizedOptions
				.filter((entry) => entry.norm.includes(q))
				.map((entry) => entry.opt);
		},

		matchedCount() { return this.matchedOptions.length; },

		// Slice renderado — capeado a `maxVisible`.
		visibleOptions() {
			return this.matchedOptions.slice(0, this.maxVisible);
		},

		hiddenCount() {
			return Math.max(0, this.matchedCount - this.visibleOptions.length);
		},

		// group → ids de todos sus items (del set completo, no del filtrado): el
		// header marca/desmarca el grupo entero aunque la búsqueda oculte algunos.
		groupIdMap() {
			const map = {};
			this.options.forEach((opt) => {
				if (opt.group == null) return;
				(map[opt.group] = map[opt.group] || []).push(opt.id);
			});
			return map;
		},

		// Lista de render: con `grouped`, interleava encabezados de grupo entre
		// los items (insertando un header al cambiar el `group`); si no, una
		// entrada `option` por item. Mantiene el orden de `visibleOptions`.
		displayRows() {
			if (!this.grouped) {
				return this.visibleOptions.map((opt) => ({ type: "option", opt }));
			}
			const rows = [];
			let currentGroup;
			this.visibleOptions.forEach((opt) => {
				if (opt.group !== currentGroup) {
					currentGroup = opt.group;
					rows.push({ type: "header", label: opt.group });
				}
				rows.push({ type: "option", opt });
			});
			return rows;
		},
	},

	methods: {
		// Label visible recortado a un límite FIJO de caracteres (no por ancho):
		// si supera LABEL_MAX_LEN se corta y se agrega elipsis.
		truncateLabel(label) {
			return label.length > this.labelMaxLen
				? `${label.slice(0, this.labelMaxLen)}…`
				: label;
		},
		// Binding del tooltip de una opción: nombre completo + theme estilizado,
		// sólo cuando el nombre supera el límite (si no, content vacío →
		// floating-vue no muestra nada). Reemplaza al `title` nativo.
		optionTooltip(label) {
			return {
				content: label.length > this.labelMaxLen ? label : "",
				theme: "entity-name",
			};
		},
		// Normalización compartida: descompone acentos (NFD), descarta las marcas
		// diacríticas y baja a minúscula. "Lácteos S.A." → "lacteos s.a.".
		normalize(str) {
			return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
		},
		isSelected(id) { return this.value.includes(id); },
		// Cantidad de items del grupo presentes en la selección actual.
		groupSelectedCount(group) {
			const ids = this.groupIdMap[group] || [];
			const selected = new Set(this.value);
			return ids.filter((id) => selected.has(id)).length;
		},
		isGroupChecked(group) {
			const ids = this.groupIdMap[group] || [];
			return ids.length > 0 && this.groupSelectedCount(group) === ids.length;
		},
		isGroupIndeterminate(group) {
			const count = this.groupSelectedCount(group);
			return count > 0 && count < (this.groupIdMap[group] || []).length;
		},
		// Toggle del grupo entero: si ya está todo seleccionado lo quita; si no,
		// agrega todos sus items (sin duplicar). Marcar el L2 muestra todas sus L3.
		toggleGroup(group) {
			const ids = this.groupIdMap[group] || [];
			if (!ids.length) return;
			let next;
			if (this.isGroupChecked(group)) {
				const drop = new Set(ids);
				next = this.value.filter((v) => !drop.has(v));
			} else {
				next = [...new Set([...this.value, ...ids])];
			}
			this.$emit("input", next);
		},
		toggle() {
			if (this.disabled) return;
			if (this.isOpen) this.close();
			else this.open();
		},
		open() {
			if (this.isOpen) return;
			this.isOpen = true;
			this.query = "";
			// Aviso de apertura (aditivo): el drawer de Rentabilidad lo usa para
			// volver principal la categoría cuyo panel se abre.
			this.$emit("open");
			// Toggle "todo o nada": abrir desde vacío marca todos los items (activa
			// la dimensión completa). No pisa una selección ya existente.
			if (this.selectAllOnOpen && this.value.length === 0 && this.options.length) {
				this.$emit("input", this.options.map((opt) => opt.id));
			}
			this.$nextTick(() => {
				document.addEventListener("mousedown", this.handleClickOutside);
				if (this.searchable && this.$refs.search) this.$refs.search.focus();
			});
		},
		close() {
			if (!this.isOpen) return;
			this.isOpen = false;
			document.removeEventListener("mousedown", this.handleClickOutside);
		},
		toggleOption(id) {
			if (this.single) {
				this.$emit("input", this.isSelected(id) ? [] : [id]);
				// Elegido el ítem, el panel ya cumplió: en selección única no
				// hay nada más que tildar.
				this.close();
				return;
			}
			const idx = this.value.indexOf(id);
			const next = idx === -1
				? [...this.value, id]
				: this.value.filter(v => v !== id);
			this.$emit("input", next);
		},
		clearAll() {
			// Modo toggle todo-o-nada: "Ver todos" vuelve a marcar todos los
			// items (mantiene la dimensión activa). Vaciar acá la desactivaría
			// —no es lo que el usuario pide— así que re-seleccionamos todo y
			// dejamos el panel abierto sobre la lista completa.
			if (this.selectAllOnOpen) {
				this.$emit("input", this.options.map((opt) => opt.id));
				return;
			}
			this.$emit("input", []);
			// Limpiar también cierra el panel: el usuario terminó con el filtro,
			// no lo dejamos abierto sobre una lista sin selección.
			this.close();
		},
		handleClickOutside(event) {
			const root = this.$refs.root;
			if (root && !root.contains(event.target)) {
				this.close();
			}
		},
	},

	beforeDestroy() {
		document.removeEventListener("mousedown", this.handleClickOutside);
	},
};
</script>

<style scoped lang="scss">
.ms_wrapper {
	position: relative;
	display: inline-flex;
}

.ms_trigger {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0 12px 0 16px;
	min-height: 34px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
	user-select: none;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		border-color: var(--report-primary);
		color: var(--report-bg);
	}

	&.is-open {
		border-color: var(--report-primary);
	}

	&:disabled {
		cursor: not-allowed;
		color: var(--report-text-muted);
		background: var(--report-surface);
	}
}

// Variante segmentada: el trigger se funde en un grupo .sub_nav_filters como
// un chip más (mismo look que .filter_btn). Sin fondo/borde propios; primary
// al activarse. Reglas posteriores al bloque base → ganan por orden a igual
// especificidad. Tokens del theme (sin hex) para igualar a los chips vecinos.
.ms_trigger--segmented {
	background: transparent;
	border: none;
	border-radius: 6px;
	min-height: 32px;
	color: var(--report-text-muted-strong);

	&:hover { background: var(--report-surface); }

	&.is-open { border-color: transparent; }

	&.is-active {
		background: var(--report-primary);
		border-color: transparent;
		color: var(--report-bg);
	}
}

.ms_label { line-height: 1; }

.ms_count_badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	background: rgba(255, 255, 255, 0.22);
	border-radius: 10px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1;

	.ms_trigger:not(.is-active) & {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.ms_chevron {
	width: 12px;
	height: 12px;
	transition: transform 0.2s ease;
	color: inherit;

	&.is-rotated { transform: rotate(180deg); }
}

.ms_panel {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 220px;

	// Anclaje a la derecha: el panel crece hacia la izquierda desde el borde
	// derecho del trigger, para no salirse de pantalla cuando el trigger está
	// pegado al borde derecho.
	&.is-right {
		left: auto;
		right: 0;
	}
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 10px;
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
	padding: 6px;
	z-index: 4;
}

.ms_search {
	padding: 2px 2px 6px;
}

.ms_search_input {
	width: 100%;
	padding: 7px 10px;
	border: 1px solid var(--report-border);
	border-radius: 6px;
	font-size: 14px;
	color: var(--report-text-muted-strong);
	outline: none;
	box-sizing: border-box;
	transition: border-color 0.12s ease;

	&:focus { border-color: var(--report-primary); }
}

.ms_options {
	list-style: none;
	margin: 0;
	padding: 0;
	// Listas largas (clientes) scrollean dentro del panel en vez de empujar la
	// página. ~7 items visibles antes del scroll.
	max-height: 260px;
	overflow-y: auto;
}

.ms_empty {
	padding: 8px 10px;
	color: var(--report-text-muted-soft);
	font-size: 13px;
	user-select: none;
}

// Encabezado de grupo (modo grouped): fila PADRE seleccionable (nivel 2 / tipo
// de contraparte). Más prominente que sus hijos —texto primario + 700, tamaño
// de fila completo— para leerse como el contenedor del que cuelgan las L3.
.ms_group_header {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 10px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 700;
	color: var(--report-text);
	user-select: none;
	transition: background 0.12s ease, color 0.12s ease;

	&:hover { background: var(--report-surface); }

	&:not(:first-child) {
		margin-top: 4px;
		border-top: 1px solid var(--report-border);
		padding-top: 10px;
	}
}

.ms_group_label { line-height: 1.2; }

// Item hijo (nivel 3) bajo un encabezado: indentado un "tab" para que su
// checkbox quede inset respecto al del padre y el grupo se lea como árbol.
// Doble clase para ganarle por especificidad al shorthand `padding` de
// `.ms_option` (definido más abajo en el archivo, misma especificidad).
.ms_option.ms_option--child {
	padding-left: 28px;
}

// Estado indeterminado del checkbox (algunos items del grupo seleccionados):
// fondo primary con una barra horizontal en vez del check.
.ms_check.is-indeterminate {
	border-color: var(--report-primary);
	background: var(--report-primary);
}

.ms_check_dash {
	width: 8px;
	height: 0;
	border-top: 2px solid var(--report-bg);
}

.ms_overflow_hint {
	margin: 2px 2px 0;
	padding: 6px 10px;
	color: var(--report-text-muted-soft);
	font-size: 12px;
	line-height: 1.3;
	border-top: 1px solid var(--report-border);
}

.ms_option {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 10px;
	border-radius: 6px;
	cursor: pointer;
	color: var(--report-text-muted-strong);
	font-size: 14px;
	font-weight: 500;
	transition: background 0.12s ease, color 0.12s ease;
	user-select: none;

	&:hover { background: var(--report-surface); }

	&.is-checked { color: var(--report-primary); }
}

.ms_check {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	border: 1.5px solid var(--report-border-soft);
	border-radius: 4px;
	background: var(--report-bg);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: border-color 0.12s ease, background 0.12s ease;

	&.is-checked {
		border-color: var(--report-primary);
		background: var(--report-primary);
	}
}

.ms_check_mark {
	width: 8px;
	height: 4px;
	border-left: 2px solid var(--report-bg);
	border-bottom: 2px solid var(--report-bg);
	transform: rotate(-45deg) translate(1px, -1px);
}

// Selección única: la casilla es un radio (redonda, con un punto en vez del
// tilde) para que se lea de entrada que elegir uno reemplaza al anterior.
.ms_check--radio {
	border-radius: 50%;

	.ms_check_mark {
		width: 6px;
		height: 6px;
		border: 0;
		border-radius: 50%;
		background: var(--report-bg);
		transform: none;
	}
}

.ms_option_label {
	line-height: 1.2;
	// El recorte es por caracteres (truncateLabel: límite fijo de 30), no por
	// ancho: una sola línea, sin ellipsis CSS (que volvería a recortar de forma
	// dinámica según el ancho del panel). El nombre completo va en el tooltip.
	white-space: nowrap;
}

// Con buscador la lista puede ser ancha (nombres de clientes); damos un cap
// para que el panel no se desborde de la pantalla.
.ms_panel { max-width: 360px; }

// Zona de acción separada de la lista por un divisor; el botón vive dentro con
// su propio chrome para leerse como control presionable, no como link de pie.
.ms_footer {
	margin-top: 6px;
	padding-top: 8px;
	border-top: 1px solid var(--report-border);
}

.ms_clear_btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 8px 12px;
	// Inset sobre el panel blanco: surface gris + borde = afordancia de botón.
	background: var(--report-surface);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	font-size: 13px;
	font-weight: 600;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease,
		color 0.12s ease, transform 0.06s ease;

	&:hover {
		background: var(--report-bg);
		border-color: var(--report-primary);
		color: var(--report-primary);
	}

	// El translate de 1px es la señal de "presionable": el botón se hunde al
	// click. Micro-interacción, sin bounce.
	&:active {
		transform: translateY(1px);
		background: var(--report-surface);
	}

	&:focus-visible {
		outline: none;
		border-color: var(--report-primary);
		// Glow del focus en el primary actual (#0d3c3f). No hay token alpha del
		// primary, así que el rgba queda literal — antes era el teal viejo
		// (#00A9AD), que ya no matchea la marca.
		box-shadow: 0 0 0 3px rgba(13, 60, 63, 0.18);
	}
}

.ms_fade-enter-active,
.ms_fade-leave-active {
	transition: opacity 0.12s ease, transform 0.12s ease;
}

.ms_fade-enter,
.ms_fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
